import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, MapPin, User, X } from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';
import { useToast } from '../components/Toast';
import { TOAST_MESSAGES, CONFIRM_MESSAGES } from '../utils/toastMessages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get auth headers
const getAuthHeaders = () => {
  const stored = sessionStorage.getItem('assistant');
  if (stored) {
    try {
      const assistant = JSON.parse(stored);
      return { 'x-assistant-session': assistant.id };
    } catch (e) {}
  }
  return {};
};

const CalendarPage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month'); // month, week, day
  const [mobileView, setMobileView] = useState('agenda'); // Default mobile to agenda
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const { success, error: showError } = useToast();
  
  // Meeting Notes state
  const [meetingNotes, setMeetingNotes] = useState('');
  const [shareWithExec, setShareWithExec] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'personal',
    start_datetime: '',
    end_datetime: '',
    location: '',
    meeting_type: '',
    meeting_link: '',
    meeting_address: '',
    all_day: false,
    executive_id: ''
  });
  const [error, setError] = useState('');

  useEffect(() => {
    fetchEvents();
    fetchExecutives();
    
    // Handle window resize for mobile detection
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/assistant/events`, {
        headers: getAuthHeaders(),
        
      });
      
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
      } else if (res.status === 401) {
        navigate('/assistant-login');
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching events:', err);
      setLoading(false);
    }
  };

  const fetchExecutives = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/executives`, {
        headers: getAuthHeaders(),
        
      });
      
      if (res.ok) {
        const data = await res.json();
        setExecutives(data.executives || []);
      }
    } catch (err) {
      console.error('Error fetching executives:', err);
    }
  };

  const fetchMeetingNotes = async (eventId) => {
    setLoadingNotes(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/events/${eventId}/notes`, {
        
      });
      if (res.ok) {
        const data = await res.json();
        if (data.notes) {
          setMeetingNotes(data.notes.notes_body || '');
          setShareWithExec(data.notes.shared_with_exec || false);
        } else {
          setMeetingNotes('');
          setShareWithExec(false);
        }
      }
    } catch (err) {
      console.error('Error fetching meeting notes:', err);
    } finally {
      setLoadingNotes(false);
    }
  };

  const saveMeetingNotes = async () => {
    if (!selectedEvent) return;
    
    setSavingNotes(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/events/${selectedEvent.id}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify({
          notes_body: meetingNotes,
          shared_with_exec: shareWithExec
        })
      });
      
      if (res.ok) {
        success('Notes saved', 'Meeting notes have been saved');
      } else {
        showError('Error', 'Failed to save notes');
      }
    } catch (err) {
      console.error('Error saving meeting notes:', err);
      showError('Error', 'Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const insertPrompt = (promptType) => {
    const prompts = {
      'agenda': '\n## Agenda\n- \n- \n- \n',
      'decisions': '\n## Key Decisions\n- \n',
      'actions': '\n## Action Items\n- [ ] \n- [ ] \n',
      'followups': '\n## Follow-ups / Risks\n- \n'
    };
    setMeetingNotes(prev => prev + prompts[promptType]);
  };

  const handleCreate = async () => {
    setError('');
    
    if (!formData.title.trim() || !formData.start_datetime || !formData.end_datetime) {
      setError('Title, start time, and end time are required');
      return;
    }

    try {
      const payload = {
        ...formData,
        executive_id: formData.executive_id || null
      };

      const res = await fetch(`${BACKEND_URL}/api/assistant/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload)
      });

      if (res.status === 401) {
        setError('Session expired. Please log in again.');
        setTimeout(() => navigate('/assistant-login'), 2000);
        return;
      }

      if (!res.ok) {
        if (res.status === 500) {
          setError('Server error. Please try again or contact admin.');
        } else if (res.status === 403) {
          setError('Unauthorized. Please check your permissions.');
        } else {
          setError(`Error ${res.status}: Failed to create event`);
        }
        return;
      }

      const data = await res.json();
      
      if (data.ok) {
        setShowCreateModal(false);
        setFormData({
          title: '',
          description: '',
          event_type: 'personal',
          start_datetime: '',
          end_datetime: '',
          location: '',
          meeting_type: '',
          meeting_link: '',
          meeting_address: '',
          all_day: false,
          executive_id: ''
        });
        fetchEvents();
      } else {
        setError(data.message || 'Failed to create event');
      }
    } catch (err) {
      console.error('Event creation error:', err);
      setError('Network error. Please check your connection and try again.');
    }
  };

  const handleEdit = async () => {
    setError('');
    
    if (!selectedEvent) return;

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        event_type: formData.event_type,
        start_datetime: formData.start_datetime,
        end_datetime: formData.end_datetime,
        location: formData.location,
        all_day: formData.all_day
      };

      const res = await fetch(`${BACKEND_URL}/api/assistant/events/${selectedEvent.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      
      if (data.ok) {
        setShowEditModal(false);
        setSelectedEvent(null);
        fetchEvents();
      } else {
        setError(data.message || 'Failed to update event');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const handleDeleteClick = () => {
    setEventToDelete(selectedEvent);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!eventToDelete) return;
    
    setIsDeleting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/events/${eventToDelete.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        setShowDeleteConfirm(false);
        setShowEditModal(false);
        setSelectedEvent(null);
        fetchEvents();
        success(TOAST_MESSAGES.EVENT_DELETED.title, TOAST_MESSAGES.EVENT_DELETED.message);
      } else {
        showError(TOAST_MESSAGES.CALENDAR_SAVE_ERROR.title, TOAST_MESSAGES.CALENDAR_SAVE_ERROR.message);
      }
    } catch (err) {
      console.error('Error deleting event:', err);
      showError(TOAST_MESSAGES.NETWORK_ERROR.title, TOAST_MESSAGES.NETWORK_ERROR.message);
    }
    setIsDeleting(false);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Previous month days
    const prevMonth = new Date(year, month, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({ date: prevMonthDays - i, isCurrentMonth: false });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: i, isCurrentMonth: true });
    }
    
    // Next month days to fill the grid
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: i, isCurrentMonth: false });
    }
    
    return days;
  };

  const getEventsForDay = (day) => {
    if (!day.isCurrentMonth) return [];
    
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const dateStr = new Date(year, month, day.date).toDateString();
    
    return events.filter(event => {
      const eventDate = new Date(event.start_datetime).toDateString();
      return eventDate === dateStr;
    });
  };

  const changeMonth = (delta) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + delta, 1));
  };

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'executive': return '#8B5CF6';
      case 'client': return '#3B82F6';
      case 'personal': return '#10B981';
      case 'internal': return '#F59E0B';  // Amber for internal meetings
      case 'admin': return '#6366F1';     // Indigo for admin meetings
      case 'assistant': return '#14B8A6'; // Teal for assistant meetings
      case 'holiday': return '#EF4444';   // Red for holidays
      default: return '#6B7280';
    }
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F8F9FA', padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#6B7280' }}>Loading calendar...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F8F9FA' }}>
      {/* Header */}
      <div style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E7EB', padding: isMobile ? '12px 16px' : '20px 32px' }}>
        <div style={{ maxWidth: '1600px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isMobile ? '12px' : '0', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: isMobile ? '12px' : '0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: isMobile ? '1 1 100%' : 'auto' }}>
              <button
                onClick={() => navigate('/a-dashboard')}
                style={{
                  padding: isMobile ? '6px' : '8px',
                  background: 'transparent',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ArrowLeft size={18} color="#6B7280" />
              </button>
              <div style={{ flex: 1 }}>
                <h1 style={{ color: '#0A0A0A', fontSize: isMobile ? '18px' : '24px', fontWeight: '600', margin: '0 0 4px 0' }}>
                  Calendar
                </h1>
                {!isMobile && (
                  <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
                    {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </p>
                )}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: isMobile ? '8px' : '12px', alignItems: 'center', flex: isMobile ? '1 1 100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button
                  onClick={() => changeMonth(-1)}
                  style={{
                    padding: isMobile ? '6px' : '8px',
                    background: 'transparent',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <ChevronLeft size={isMobile ? 16 : 18} color="#6B7280" />
                </button>
                <button
                  onClick={() => setCurrentDate(new Date())}
                  style={{
                    padding: isMobile ? '6px 12px' : '8px 16px',
                    background: 'transparent',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: isMobile ? '13px' : '14px',
                    color: '#374151',
                    fontWeight: '500'
                  }}
                >
                  Today
                </button>
                <button
                  onClick={() => changeMonth(1)}
                  style={{
                    padding: isMobile ? '6px' : '8px',
                    background: 'transparent',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <ChevronRight size={18} color="#6B7280" />
                </button>
              </div>
              
              <button
                onClick={() => {
                  const now = new Date();
                  const startTime = new Date(now.getTime() + 60 * 60 * 1000).toISOString().slice(0, 16);
                  const endTime = new Date(now.getTime() + 2 * 60 * 60 * 1000).toISOString().slice(0, 16);
                  
                  setFormData({
                    title: '',
                    description: '',
                    event_type: 'personal',
                    start_datetime: startTime,
                    end_datetime: endTime,
                    location: '',
                    all_day: false,
                    executive_id: ''
                  });
                  setError('');
                  setShowCreateModal(true);
                }}
                style={{
                  padding: '10px 16px',
                  background: '#3B82F6',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Plus size={isMobile ? 16 : 18} />
                {isMobile ? '+' : 'New Event'}
              </button>
            </div>
          </div>

          {/* Legend - Desktop Only */}
          {!isMobile && (
            <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#10B981' }} />
                <span style={{ fontSize: '13px', color: '#6B7280' }}>Personal</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#8B5CF6' }} />
                <span style={{ fontSize: '13px', color: '#6B7280' }}>Executive</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#3B82F6' }} />
                <span style={{ fontSize: '13px', color: '#6B7280' }}>Client</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Calendar Content - Conditional View */}
      <div style={{ maxWidth: '1600px', margin: '0 auto', padding: isMobile ? '16px' : '24px 32px' }}>
        {isMobile ? (
          /* Mobile: Agenda View ONLY */
          /* Agenda View */
          <div style={{ background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
            {(() => {
              // Get events for current month and next 30 days sorted by date
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const thirtyDaysAgo = new Date(today);
              thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
              
              const upcomingEvents = events
                .filter(e => {
                  const eventDate = new Date(e.start_datetime);
                  return eventDate >= thirtyDaysAgo;  // Show events from last 30 days
                })
                .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
              
              if (upcomingEvents.length === 0) {
                return (
                  <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                    <CalendarIcon size={48} color="#E5E7EB" style={{ margin: '0 auto 16px' }} />
                    <h3 style={{ color: '#374151', fontSize: '16px', fontWeight: '500', margin: '0 0 8px 0' }}>
                      No Upcoming Events
                    </h3>
                    <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
                      Add one with the "New Event" button
                    </p>
                  </div>
                );
              }

              // Group events by date
              const groupedEvents = {};
              upcomingEvents.forEach(event => {
                const dateKey = new Date(event.start_datetime).toLocaleDateString('en-US', { 
                  weekday: 'short', 
                  month: 'short', 
                  day: 'numeric' 
                });
                if (!groupedEvents[dateKey]) groupedEvents[dateKey] = [];
                groupedEvents[dateKey].push(event);
              });

              const getEventColor = (type) => {
                switch(type) {
                  case 'personal': return { bg: '#D1FAE5', text: '#065F46', label: 'Personal' };
                  case 'executive': return { bg: '#E9D5FF', text: '#6B21A8', label: 'Executive' };
                  case 'client': return { bg: '#DBEAFE', text: '#1E40AF', label: 'Client' };
                  case 'internal': return { bg: '#FEF3C7', text: '#92400E', label: 'Internal' };
                  case 'admin': return { bg: '#E0E7FF', text: '#3730A3', label: 'Admin' };
                  case 'assistant': return { bg: '#CCFBF1', text: '#0F766E', label: 'Assistant' };
                  case 'holiday': return { bg: '#FEE2E2', text: '#991B1B', label: 'Holiday' };
                  default: return { bg: '#F3F4F6', text: '#374151', label: 'Event' };
                }
              };

              return (
                <div>
                  {Object.entries(groupedEvents).map(([dateHeader, dayEvents]) => (
                    <div key={dateHeader}>
                      {/* Date Header */}
                      <div style={{ 
                        padding: '12px 16px', 
                        background: '#F9FAFB', 
                        borderBottom: '1px solid #E5E7EB',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10
                      }}>
                        <h3 style={{ color: '#374151', fontSize: '13px', fontWeight: '600', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {dateHeader}
                        </h3>
                      </div>
                      
                      {/* Events for this date */}
                      {dayEvents.map((event, idx) => {
                        const color = getEventColor(event.event_type);
                        const startTime = new Date(event.start_datetime).toLocaleTimeString('en-US', { 
                          hour: 'numeric', 
                          minute: '2-digit' 
                        });
                        const endTime = new Date(event.end_datetime).toLocaleTimeString('en-US', { 
                          hour: 'numeric', 
                          minute: '2-digit' 
                        });

                        return (
                          <button
                            key={event.id}
                            onClick={() => {
                              setSelectedEvent(event);
                              setFormData({
                                title: event.title,
                                description: event.description || event.notes || '',
                                event_type: event.event_type || event.category || 'personal',
                                start_datetime: new Date(event.start_datetime).toISOString().slice(0, 16),
                                end_datetime: new Date(event.end_datetime).toISOString().slice(0, 16),
                                location: typeof event.location === 'object' ? (event.location.type || event.location.address || '') : (event.location || ''),
                                meeting_type: event.meeting_type || (typeof event.location === 'object' ? event.location.type : '') || '',
                                meeting_link: event.meeting_link || (typeof event.location === 'object' ? event.location.link : '') || '',
                                meeting_address: event.meeting_address || (typeof event.location === 'object' ? event.location.address : '') || '',
                                all_day: event.all_day || false,
                                executive_id: event.executive_id || ''
                              });
                              setError('');
                              setMeetingNotes('');
                              setShareWithExec(false);
                              fetchMeetingNotes(event.id);
                              setShowEditModal(true);
                            }}
                            style={{
                              width: '100%',
                              padding: '16px',
                              background: '#FFFFFF',
                              border: 'none',
                              borderBottom: idx < dayEvents.length - 1 ? '1px solid #F3F4F6' : 'none',
                              cursor: 'pointer',
                              textAlign: 'left',
                              display: 'flex',
                              gap: '12px',
                              alignItems: 'flex-start',
                              transition: 'background 0.15s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#F9FAFB'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#FFFFFF'}
                          >
                            {/* Time */}
                            <div style={{ minWidth: '70px', paddingTop: '2px' }}>
                              <div style={{ color: '#374151', fontSize: '14px', fontWeight: '500' }}>
                                {startTime}
                              </div>
                              <div style={{ color: '#9CA3AF', fontSize: '12px', marginTop: '2px' }}>
                                {endTime}
                              </div>
                            </div>

                            {/* Event Details */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                <h4 style={{ color: '#0A0A0A', fontSize: '15px', fontWeight: '500', margin: 0 }}>
                                  {event.title}
                                </h4>
                                <span style={{
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  background: color.bg,
                                  color: color.text
                                }}>
                                  {color.label}
                                </span>
                              </div>
                              {event.description && (
                                <p style={{ color: '#6B7280', fontSize: '13px', margin: '4px 0', lineHeight: '1.4' }}>
                                  {event.description.substring(0, 100)}{event.description.length > 100 ? '...' : ''}
                                </p>
                              )}
                              {event.location && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '6px' }}>
                                  <MapPin size={12} color="#9CA3AF" />
                                  <span style={{ color: '#6B7280', fontSize: '13px' }}>
                                    {typeof event.location === 'object' ? (event.location.type || event.location.address || '') : event.location}
                                  </span>
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        ) : (
          /* Desktop: Month Grid View */
          <div style={{ 
            background: '#FFFFFF', 
            borderRadius: '12px', 
            border: '1px solid #E5E7EB', 
            overflow: isMobile ? 'auto' : 'hidden' 
          }}>
            {/* Day headers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #E5E7EB', background: '#F9FAFB' }}>
              {dayNames.map(day => (
                <div key={day} style={{ 
                  padding: isMobile ? '8px 4px' : '12px', 
                  textAlign: 'center', 
                  fontSize: isMobile ? '11px' : '13px', 
                  fontWeight: '600', 
                  color: '#6B7280', 
                  textTransform: 'uppercase', 
                  letterSpacing: '0.05em' 
                }}>
                  {isMobile ? day.slice(0, 1) : day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', minWidth: isMobile ? '100%' : 'auto' }}>
            {getDaysInMonth(currentDate).map((day, index) => {
              const dayEvents = getEventsForDay(day);
              const isToday = day.isCurrentMonth && 
                day.date === new Date().getDate() && 
                currentDate.getMonth() === new Date().getMonth() &&
                currentDate.getFullYear() === new Date().getFullYear();

              const maxEvents = isMobile ? 2 : 3;

              return (
                <div
                  key={index}
                  style={{
                    minHeight: isMobile ? '60px' : '100px',
                    padding: isMobile ? '4px' : '8px',
                    borderRight: (index + 1) % 7 !== 0 ? '1px solid #E5E7EB' : 'none',
                    borderBottom: index < 35 ? '1px solid #E5E7EB' : 'none',
                    background: day.isCurrentMonth ? '#FFFFFF' : '#F9FAFB'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: isMobile ? '20px' : '28px',
                    height: isMobile ? '20px' : '28px',
                    borderRadius: '50%',
                    background: isToday ? '#3B82F6' : 'transparent',
                    color: isToday ? '#FFFFFF' : day.isCurrentMonth ? '#374151' : '#9CA3AF',
                    fontSize: isMobile ? '11px' : '13px',
                    fontWeight: isToday ? '600' : '500',
                    marginBottom: isMobile ? '2px' : '4px'
                  }}>
                    {day.date}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '2px' : '4px' }}>
                    {dayEvents.slice(0, maxEvents).map(event => (
                      <button
                        key={event.id}
                        onClick={() => {
                          setSelectedEvent(event);
                          setFormData({
                            title: event.title,
                            description: event.description || event.notes || '',
                            event_type: event.event_type || event.category || 'personal',
                            start_datetime: new Date(event.start_datetime).toISOString().slice(0, 16),
                            end_datetime: new Date(event.end_datetime).toISOString().slice(0, 16),
                            location: typeof event.location === 'object' ? (event.location.type || event.location.address || '') : (event.location || ''),
                            meeting_type: event.meeting_type || (typeof event.location === 'object' ? event.location.type : '') || '',
                            meeting_link: event.meeting_link || (typeof event.location === 'object' ? event.location.link : '') || '',
                            meeting_address: event.meeting_address || (typeof event.location === 'object' ? event.location.address : '') || '',
                            all_day: event.all_day || false,
                            executive_id: event.executive_id || ''
                          });
                          setError('');
                          setMeetingNotes('');
                          setShareWithExec(false);
                          fetchMeetingNotes(event.id);
                          setShowEditModal(true);
                        }}
                        style={{
                          padding: '4px 6px',
                          background: getEventTypeColor(event.event_type || event.category),
                          color: '#FFFFFF',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '500',
                          textAlign: 'left',
                          cursor: 'pointer',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {event.title}
                      </button>
                    ))}
                    {dayEvents.length > maxEvents && (
                      <span style={{ fontSize: isMobile ? '9px' : '11px', color: '#6B7280', marginLeft: '4px', display: 'block' }}>
                        +{dayEvents.length - maxEvents} more
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => setShowCreateModal(false)}>
          <div style={{
            background: '#FFFFFF',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            border: '1px solid #E5E7EB',
            maxHeight: '90vh',
            overflowY: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>
              Create New Event
            </h3>

            {error && (
              <div style={{
                background: '#FEF2F2',
                border: '1px solid #FCA5A5',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                color: '#991B1B',
                fontSize: '13px'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Event Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Meeting, appointment, etc."
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Add details..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                    Start Time *
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.start_datetime}
                    onChange={(e) => setFormData({ ...formData, start_datetime: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                    End Time *
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.end_datetime}
                    onChange={(e) => setFormData({ ...formData, end_datetime: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Meeting Type / Location
                </label>
                <select
                  value={formData.meeting_type}
                  onChange={(e) => setFormData({ ...formData, meeting_type: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select meeting type...</option>
                  <option value="zoom">Zoom</option>
                  <option value="google_meet">Google Meet</option>
                  <option value="phone">Phone Call</option>
                  <option value="in_person">In Person</option>
                  <option value="other">Other</option>
                </select>

                {/* Conditional fields based on meeting type */}
                {(formData.meeting_type === 'zoom' || formData.meeting_type === 'google_meet') && (
                  <input
                    type="text"
                    value={formData.meeting_link}
                    onChange={(e) => setFormData({ ...formData, meeting_link: e.target.value })}
                    placeholder={`${formData.meeting_type === 'zoom' ? 'Zoom' : 'Google Meet'} link`}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      marginTop: '8px'
                    }}
                  />
                )}

                {formData.meeting_type === 'phone' && (
                  <input
                    type="text"
                    value={formData.meeting_link}
                    onChange={(e) => setFormData({ ...formData, meeting_link: e.target.value })}
                    placeholder="Phone number"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      marginTop: '8px'
                    }}
                  />
                )}

                {formData.meeting_type === 'in_person' && (
                  <textarea
                    value={formData.meeting_address}
                    onChange={(e) => setFormData({ ...formData, meeting_address: e.target.value })}
                    placeholder="Address (Street, City, State, Zip)"
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      resize: 'vertical',
                      marginTop: '8px'
                    }}
                  />
                )}

                {formData.meeting_type === 'other' && (
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Location details"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      marginTop: '8px'
                    }}
                  />
                )}
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Event Type
                </label>
                <select
                  value={formData.event_type}
                  onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="personal">Personal</option>
                  <option value="executive">Executive</option>
                  <option value="client">Client</option>
                </select>
              </div>

              {executives.length > 0 && formData.event_type === 'executive' && (
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                    Related Executive
                  </label>
                  <select
                    value={formData.executive_id}
                    onChange={(e) => setFormData({ ...formData, executive_id: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">None</option>
                    {executives.map(exec => (
                      <option key={exec.id} value={exec.id}>{exec.full_name}</option>
                    ))}
                  </select>
                </div>
              )}

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.all_day}
                  onChange={(e) => setFormData({ ...formData, all_day: e.target.checked })}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px', color: '#374151' }}>All day event</span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'transparent',
                  color: '#6B7280',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#3B82F6',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Create Event
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedEvent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => setShowEditModal(false)}>
          <div style={{
            background: '#FFFFFF',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            border: '1px solid #E5E7EB',
            maxHeight: '90vh',
            overflowY: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', margin: 0 }}>
                Edit Event
              </h3>
              <button
                onClick={handleDeleteClick}
                style={{
                  padding: '6px 12px',
                  background: '#FEE2E2',
                  color: '#DC2626',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Delete
              </button>
            </div>

            {error && (
              <div style={{
                background: '#FEF2F2',
                border: '1px solid #FCA5A5',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                color: '#991B1B',
                fontSize: '13px'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Event Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                    Start Time *
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.start_datetime}
                    onChange={(e) => setFormData({ ...formData, start_datetime: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                    End Time *
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.end_datetime}
                    onChange={(e) => setFormData({ ...formData, end_datetime: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Location
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Event Type
                </label>
                <select
                  value={formData.event_type}
                  onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="personal">Personal</option>
                  <option value="executive">Executive</option>
                  <option value="client">Client</option>
                </select>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.all_day}
                  onChange={(e) => setFormData({ ...formData, all_day: e.target.checked })}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px', color: '#374151' }}>All day event</span>
              </label>
              {/* Meeting Notes Section */}
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #E5E7EB' }}>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
                  Meeting Notes
                </label>
                
                {/* Quick Prompts */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                  <button
                    type="button"
                    onClick={() => insertPrompt('agenda')}
                    style={{ padding: '4px 10px', background: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', color: '#374151' }}
                  >
                    + Agenda
                  </button>
                  <button
                    type="button"
                    onClick={() => insertPrompt('decisions')}
                    style={{ padding: '4px 10px', background: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', color: '#374151' }}
                  >
                    + Decisions
                  </button>
                  <button
                    type="button"
                    onClick={() => insertPrompt('actions')}
                    style={{ padding: '4px 10px', background: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', color: '#374151' }}
                  >
                    + Action Items
                  </button>
                  <button
                    type="button"
                    onClick={() => insertPrompt('followups')}
                    style={{ padding: '4px 10px', background: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', color: '#374151' }}
                  >
                    + Follow-ups
                  </button>
                </div>

                {loadingNotes ? (
                  <div style={{ padding: '16px', textAlign: 'center', color: '#6B7280', fontSize: '13px' }}>
                    Loading notes...
                  </div>
                ) : (
                  <textarea
                    value={meetingNotes}
                    onChange={(e) => setMeetingNotes(e.target.value)}
                    placeholder="Add meeting notes, agenda, action items..."
                    rows={5}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '13px',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      lineHeight: '1.5'
                    }}
                  />
                )}

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={shareWithExec}
                      onChange={(e) => setShareWithExec(e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>Share with Executive</span>
                  </label>
                  <button
                    type="button"
                    onClick={saveMeetingNotes}
                    disabled={savingNotes}
                    style={{
                      padding: '6px 12px',
                      background: savingNotes ? '#E5E7EB' : '#10B981',
                      color: savingNotes ? '#9CA3AF' : '#FFFFFF',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: savingNotes ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {savingNotes ? 'Saving...' : 'Save Notes'}
                  </button>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedEvent(null);
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'transparent',
                  color: '#6B7280',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleEdit}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#3B82F6',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        {...CONFIRM_MESSAGES.DELETE_EVENT}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default CalendarPage;
